import styled from 'styled-components';

import * as A from '../appTable/styles';

/**
 * The tree table's surface is AppTable's surface.
 *
 * This component missed the Step 3 pass and kept its own copy of everything —
 * 14px cells, `#2d3748` / `#f0f0f0` / `#4d4d4d`, `padding: 14px 20px` rows and
 * a private white loading scrim. Rather than restate the design a second time
 * and let the two drift apart again, it now re-exports AppTable's parts and
 * overrides only what a tree needs on top of them.
 *
 * The one thing that is genuinely different is row visibility: a collapsed
 * branch's rows stay in the DOM and are hidden with `visibility: collapse`,
 * toggled by class from the component. Everything else — the header
 * treatment, --h-row, the hairlines, the hover ground, the alignment classes,
 * the row-action icons — is AppTable's, unchanged.
 */

export const TableCon = A.TableCon;
export const Table = A.Table;
export const Thead = A.Thead;
export const Th = A.Th;
export const HeaderTh = A.HeaderTh;
export const Td = A.Td;
export const RowActions = A.RowActions;
export const Pagination = A.Pagination;
export const Status = A.Status;
export const Div = A.Div;

/**
 * A row.
 *
 * `visibility: collapse` on a `<tr>` removes it from the table's layout
 * without taking it out of the DOM, which is what lets the expand/collapse
 * toggle work by class alone. It needs `border-collapse: collapse` on the
 * table to behave, which AppTable's `Table` already sets.
 */
export const Tr = styled(A.Tr)`
  &.hide {
    visibility: collapse;
  }

  &.show {
    visibility: visible;
  }
`;

/*
 * Declared after Tr and Td so the row rules can reference them.
 *
 * The separator here is an inset shadow on the TOP of a cell, where AppTable
 * uses a bottom border. Two problems forced both halves of that.
 *
 * AppTable can drop its last separator with `:last-child` because its last
 * DOM row is its last visible row. A tree's is not: a collapsed branch keeps
 * its children in the DOM after their parent and hides them with
 * `visibility: collapse`, so `:last-child` lands on a row nobody can see and
 * the last *visible* row keeps a hairline under it. Confirmed in the browser
 * on Ticket Categories, under a collapsed `IT Problem`. Hanging the
 * separator off the top instead moves it onto the row below, and the first
 * row is always a root — never hidden — so `:first-child` is always the
 * right row to strip.
 *
 * That alone was not enough: under `border-collapse: collapse` a hidden
 * row still contributes its border to the collapsed edge, so Chrome went on
 * painting the same hairline. A box-shadow takes no part in border collapsing
 * and is not painted at all for a `visibility: collapse` row, so the line
 * appears only where a row actually appears. Same one pixel, same colour.
 */
export const Tbody = styled.tbody`
  ${Tr}:hover {
    background: var(--surface-alt);
  }

  ${Tr} > ${Td} {
    border-top: none;
    border-bottom: none;
    box-shadow: inset 0 1px 0 var(--line);
  }

  ${Tr}:first-child > ${Td} {
    box-shadow: none;
  }
`;
